import React, { useState } from 'react';
import Section, {Sections} from '../../Components/Containers/Section';
import Grid from '../../Components/Formatting/Grid';
import NoValue from '../../Components/EmptyStates/NoValue';
import Money from '../../Components/Formatting/Money';
import Services from '../../Components/Formatting/Services';
import Duration from '../../Components/Formatting/Duration';
import DateTime from '../../Components/Formatting/DateTime';
import RoundName from '../../Components/Formatting/RoundName';
import Loading from '../../Components/Loading';
import ScheduleJob from './ScheduleJob';
import MarkAsDone from './MarkAsDone';
import EditJob from './EditJob';
import ScheduleConstraints from '../../Components/Formatting/ScheduleConstraints';
import NoItems from '../../Components/EmptyStates/NoItems';
import UserName from '../../Components/Formatting/UserName';
import { useDialogues } from '../../Components/Dialogues';
import Text from '../../Components/Formatting/Text';
import Skip from './Skip';
import Time from '../../Components/Formatting/Time';
import JobHistory from './JobHistory';
import Week from '../../Components/Formatting/Week';
import EditItemisationAdjustment from './EditItemisationAdjustment';
import TimeAgo from '../../Components/Formatting/TimeAgo';
import Tags from '../../Components/Tags/Tags';
import { useSession } from '../../Components/Session';

const Jobs = ({customer, deleteJob, cancelJob, resumeJob, refresh}) => {
    const [scheduling, setScheduling] = useState(null);
    const [markingAsDone, setMarkingAsDone] = useState(null);
    const [skipping, setSkipping] = useState(null);
    const [editing, setEditing] = useState(null);
    const [adjusting, setAdjusting] = useState(null);
    const [viewingHistory, setViewingHistory] = useState(false);
    const {confirm} = useDialogues();
    const {hasFeature} = useSession();

    const customerCoordinates = {latitude: customer.latitude, longitude: customer.longitude};

    if (!customer.jobs) {
        return <Loading size="4x" center />;
    }

    return <>
        <Sections divider>
            {customer.jobs.sort((a, b) => b.priority - a.priority).map(job => {
                return <Section key={job.id}
                        title={job.title ?? '#'+job.shortId}
                        className="mb-6"
                        menuDisabled={!!customer?.deletedAt}
                        menu={[
                            {title: 'Schedule job', icon: 'CalendarPlus', onClick: () => setScheduling(job)},
                            {title: 'Mark as done', icon: 'Check', onClick: () => setMarkingAsDone(job)},
                            {title: 'Skip job', icon: 'Forward', onClick: () => setSkipping(job)},
                            {div: true},
                            {title: 'View history', icon: 'ListUl', onClick: () => setViewingHistory(job)},
                            {title: 'Edit job', icon: 'Pencil', onClick: () => setEditing({...job, coordinates: customerCoordinates})},
                            {title: 'Edit cost', icon: 'FileLines', to: `/manage/customers/${job.customerId}/itemisations/${job.itemisationId}`},
                            {title: 'Invoice Adjustment', icon: 'FilePlusMinus', onClick: () => setAdjusting(job)},
                            {div: true},
                            {title: 'Cancel job', icon: 'Ban', hidden: job.status === 'Cancelled', onClick: () => confirm(
                                {
                                    theme: 'danger',
                                    title: 'Cancel job #'+job.shortId,
                                    cancelLabel: 'Keep job',
                                    confirmLabel: 'Cancel job',
                                    message: `Are you sure you want to cancel this job? The job will still appear in the customer's account but it will not be scheduled again.\n\nYou can manually schedule this job again to resume it.`,
                                },
                                () => cancelJob(job.id).then(refresh),
                            )},
                            {title: 'Resume job', icon: 'Play', hidden: job.status !== 'Cancelled', onClick: () => confirm(
                                {
                                    theme: 'primary',
                                    title: 'Resume job #'+job.shortId,
                                    confirmLabel: 'Resume job',
                                    message: `Are you sure you want to resume this job? It will become active again and can then be scheduled as normal.`,
                                },
                                () => resumeJob(job.id).then(refresh),
                            )},
                            {title: 'Delete job', icon: 'Trash', onClick: () => confirm(
                                {
                                    theme: 'danger',
                                    title: 'Delete job #'+job.shortId,
                                    confirmLabel: 'Delete',
                                    message: 'Are you sure you want to delete this job? This will completely remove it from the customer. This cannot be undone.',
                                },
                                () => deleteJob(job.id).then(refresh),
                            )},
                    ]}>
                    <Grid items={[
                        [
                            <strong>Title</strong>,
                            job.title,
                            !!job.title
                        ],
                        [
                            <strong>Services</strong>,
                            <Services services={job.services} />,
                            !!job.services.length
                        ],
                        [
                            <strong>Tags</strong>,
                            <Tags tags={job.tags} />,
                            !!job.tags?.length && hasFeature('TAGS')
                        ],
                        [
                            <strong>Frequency</strong>,
                            job.frequency ?? <NoValue />,
                        ],
                        [
                            <strong>Cost</strong>,
                            <Money pence={job.cost} />,
                        ],
                        [
                            <strong>One-off adjustment</strong>,
                            job.itemisationAdjustment ? <>
                                <Money pence={job.itemisationAdjustment.amount} /><br />
                                <span className="text-muted">{job.itemisationAdjustment.description}</span>
                            </> : <NoValue />,
                            !!job.itemisationAdjustment
                        ],
                        [
                            <strong>Status</strong>,
                            job.status,
                        ],
                        [
                            <strong>Scheduling</strong>,
                            <ScheduleConstraints constraints={job.scheduleConstraints} />,
                            Object.values(job.scheduleConstraints ?? {}).filter(v => !!v).length > 0
                        ],
                        [
                            <strong>Assigned To</strong>,
                            <UserName userId={job.assignedTo} />,
                            !!job.assignedTo
                        ],
                        [
                            <strong>Duration</strong>,
                            job.duration ? <Duration time={job.duration} /> : <NoValue />,
                            !!job.duration
                        ],
                        [
                            <strong>Round</strong>,
                            job.roundId && <RoundName roundId={job.roundId} />,
                            !!job.roundId
                        ],
                        [
                            <strong>Last done</strong>,
                            job.lastDone ? <TimeAgo dateTime={job.lastDone} /> : <NoValue text="Never" />,
                        ],
                        [
                            <strong>Next due</strong>,
                            job.dueWeek ? <Week week={job.dueWeek} relative hideTime /> : <NoValue />,
                            !!job.dueWeek,
                        ],
                        [
                            <strong>Scheduled</strong>,
                            job.scheduledDate ? <DateTime date={job.scheduledDate} hideTime longNames /> : <NoValue />,
                            !!job.scheduledDate
                        ],
                        [
                            <strong>ETA</strong>,
                            <Time time={job.estimatedTime} />,
                            !!job.estimatedTime,
                        ],
                        [
                            <strong>Notes</strong>,
                            <Text text={job.notes} />,
                            !!job.notes
                        ],
                        [
                            <strong>Customer Skips</strong>,
                            job.skips ? <Grid items={[
                                [
                                    <strong>All time</strong>,
                                    job.skips?.allTimeCustomerSkips ?? 0
                                ],
                                [
                                    <strong>Current Streak</strong>,
                                    job.skips?.consecutiveCustomerSkips ?? 0
                                ],
                            ]} /> : <NoValue />,
                            !!job.skips
                        ],
                    ]} />
                </Section>
            })}
        </Sections>

        <NoItems icon="Truck"
        title="Add a job"
        disabled={!!customer?.deletedAt}
        onClick={() => setEditing({
            customerId: customer.id,
            coordinates: customerCoordinates,
        })}
        />

        <JobHistory job={viewingHistory} onClose={() => setViewingHistory(null)} />
        <ScheduleJob job={scheduling} onClose={() => setScheduling(null)} then={refresh} />
        <MarkAsDone job={markingAsDone} onClose={() => setMarkingAsDone(null)} then={refresh} />
        <Skip job={skipping} onClose={() => setSkipping(null)} then={refresh} />
        <EditJob job={editing} onClose={() => setEditing(null)} then={refresh} />
        <EditItemisationAdjustment job={adjusting} onClose={() => setAdjusting(null)} then={refresh} />
    </>
}

export default Jobs;
